<template>
  <transition name="fade" mode="out-in">
    <div v-if="displayed" class="indicator-display-config dark-overlay h-v-centerer">
      <div class="dark-overlay-content large">
        <div class="f-w indicator-display-config__content">
          <!-- Config list -->
          <div v-if="editingConfig == null" class="f-w indicator-display-config__content__wrapper">
            <CloseButton absolute @click.native="onClose" />
            <h2 class="basic-sub-title">
              {{ $t('message.IDC_AVAILABLE') }}
            </h2>
            <div
              v-for="config in availableDefaultConfigs"
              :key="`def-${config.id}`"
              class="idc-config v-centerer generic-border generic-border--card generic-border--clickable"
              @click="selectConfig(config)"
            >
              <div class="name">
                {{ config.name }}
              </div>
              <div class="idc-config__edit h-v-centerer greyed-out">
                <button
                  class="generic-border--icon-button generic-border--blue-border generic-border--disabled"
                  @click.stop=""
                >
                  <Icon>{{ editMedium }}</Icon>
                </button>
              </div>

              <div class="idc-config__delete h-v-centerer greyed-out">
                <button
                  class="generic-border--icon-button generic-border--blue-border generic-border--disabled"
                  @click.stop=""
                >
                  <Icon>{{ deleteMedium }}</Icon>
                </button>
              </div>
            </div>
            <h2 class="basic-sub-title custom">
              {{ $t('message.IDC_CUSTOM') }}
            </h2>
            <div
              v-for="config in compConfigs"
              :key="config.id"
              class="idc-config v-centerer generic-border generic-border--card generic-border--clickable"
              @click="selectConfig(config)"
            >
              <div class="name">
                {{ config.name }}
              </div>
              <div class="idc-config__edit h-v-centerer">
                <button
                  class="generic-border--icon-button generic-border--blue-border"
                  @click.stop="editConfig(config)"
                >
                  <Icon>{{ editMedium }}</Icon>
                </button>
              </div>

              <div class="idc-config__delete h-v-centerer">
                <button
                  class="generic-border--icon-button generic-border--blue-border"
                  @click.stop="deleteConfig(config)"
                >
                  <Icon>{{ deleteMedium }}</Icon>
                </button>
              </div>
            </div>
            <div class="generic-border--card generic-border--clickable idc-config v-centerer" @click="newConfig">
              {{ $t('message.IDC_NEW') }}
            </div>
          </div>


          <!-- Config edition -->
          <div v-else class="idc-edition">
            <CloseButton @click.native="editionDone" />
            <h2 class="basic-sub-title">
              {{ $t('message.IDC_EDITION') }}
            </h2>

            <div class="idc-edition__wrapper">
              <EditableLabel
                v-model="editingConfig.name"
                :legend="$t('message.IDC_NAME') + $t('message.TWO_DOTS')"
                use-textarea
                @input="configUpdated"
              />
              <Switcher v-model="idcMode" :titles="idcModes" @input="switchMode" />

              <!-- Pick among presets -->
              <div v-if="idcMode == 0">
                <!-- eslint-disable-next-line vue/no-v-html -->
                <div class="mt5" v-html="$t('message.IDC_SIMPLIFIED_PRESETS_HINT')" />
                <div class="mt10 legend">
                  {{ $t('message.IDC_PRESETS') + $t('message.TWO_DOTS') }}
                </div>
                <div
                  v-for="preset in presets"
                  :key="preset.presetId"
                  class="idc-preset generic-border--small-text-btn generic-border--blue-border"
                  :class="{ selected: idcAreEqual(editingConfig, preset) }"
                  @click="presetPicked(preset)"
                >
                  <div class="name">
                    {{ preset.name }}
                  </div>
                </div>
              </div>
              <!-- Advanced options -->
              <div v-else class="advanced-form">
                <!-- Allow editing all options -->
                <!-- Chart points period -->
                <div class="align-top point-period">
                  <span class="legend">
                    {{ $t('message.IDC_POINT_PERIOD') + $t('message.TWO_DOTS') }}
                  </span>
                  <InputNumber
                    v-model="editingConfig.pointPeriod"
                    @input="configUpdated"
                  />
                  <EditableLabelRadio
                    v-model="pointUnitId"
                    class="ml5"
                    :items="periodItems('pointPeriod')"
                    label-key="unit"
                    @input="configUpdated"
                  />
                </div>
                <div class="field-hint">
                  {{ pointPeriodHint }}
                </div>
                <!-- Chart period -->
                <div class="align-top point-period mt10em">
                  <span class="legend">
                    {{ $t('message.IDC_CHART_PERIOD') + $t('message.TWO_DOTS') }}
                  </span>
                  <InputNumber
                    v-model="editingConfig.chartPeriod"
                    @input="configUpdated"
                  />
                  <EditableLabelRadio
                    v-model="chartUnitId"
                    class="ml5"
                    :items="periodItems('chartPeriod')"
                    label-key="unit"
                    @input="configUpdated"
                  />
                </div>
                <!-- eslint-disable-next-line vue/no-v-html -->
                <div class="field-hint" v-html="chartPeriodHint" />
                <!-- Increasing values option -->
                <div>
                  <div class="sep" />
                  <IntCheckbox
                    v-model="editingConfig.increasingValues"
                    :title="$t('message.IDC_INCR_CUMUL_VALUES')"
                    @input="configUpdated"
                  />
                  <div class="field-hint">
                    {{ $t('message.IDC_INCR_CUMUL_VALUES_HINT') }}
                  </div>
                  <div class="increase-interval mt10" :class="{ 'greyed-out': !valuesIncreasing }">
                    <!-- If increasing values, dsplay increase interval options -->
                    <div class="align-top point-period">
                      <span class="legend">
                        {{ $t('message.IDC_INCREASE_INTERVAL') + $t('message.TWO_DOTS') }}
                      </span>
                      <InputNumber
                        v-model="editingConfig.increasePeriod"
                        :disabled="!valuesIncreasing"
                        @input="configUpdated"
                      />
                      <EditableLabelRadio
                        v-model="increaseUnitId"
                        class="ml5"
                        :items="periodItems('increasePeriod')"
                        label-key="unit"
                        :disabled="!valuesIncreasing"
                        @input="configUpdated"
                      />
                    </div>
                    <div class="field-hint">
                      {{ increasePeriodHint }}
                    </div>
                    <IntCheckbox
                      v-model="editingConfig.increasingObjectives"
                      :title="$t('message.IDC_INCR_CUMUL_OBJ')"
                      :disabled="!valuesIncreasing"
                      @input="configUpdated"
                    />
                    <div class="field-hint">
                      {{ $t('message.IDC_INCR_CUMUL_OBJ_HINT') }}
                    </div>
                  </div>
                </div>
                <div class="sep" />
                <!-- End date of the chart -->
                <!-- Either a fixed end date or today (minus a gap) -->
                <div class="legend">
                  {{ `${$t('message.IDC_END_DATE')}${$t('message.TWO_DOTS')}` }}
                </div>
                <RadioGroup
                  v-model="endDateSelectedOption"
                  :items="endDateOptions"
                  class="idc__end-date"
                >
                  <input
                    ref="chartEndDate"
                    slot="2"
                    v-model="chartEndDateFormatted"
                    class="ml5 config-start"
                    type="text"
                    readonly
                  >
                  <div slot="1" class="v-centerer ml10em">
                    <InputNumber slot="1" v-model="dateGapFromCurrent" />
                    <EditableLabelRadio
                      v-model="dateGapUnitId"
                      class="ml5"
                      :items="periodItems('dateGapFromCurrent')"
                      label-key="unit"
                      @input="configUpdated"
                    />
                  </div>
                </RadioGroup>
                <!-- eslint-disable-next-line vue/no-v-html -->
                <div class="field-hint mt5em" v-html="$t('message.IDC_END_DATE_HINT')" />
                <!-- TODO: Allow editing indicators objectives for specific periods. eg: Holidays, days off... -->
              </div>
              <div class="idc__footer">
                <div v-if="editingConfig != null" class="saved-indicator">
                  <div v-if="saving">
                    <span>{{ $t('message.IDC_SAVING') }}</span>
                  </div>
                  <div v-else class="v-centerer">
                    <img src="@/assets/img/tick-medium-green.png" alt="">
                    <span class="ml5">{{ $t('message.IDC_SAVED') }}</span>
                  </div>
                </div>
                <div class="h-v-centerer">
                  <button class="action-btn green indicator-display-config__done" @click="editionDone">
                    {{ $t('message.DONE') }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
/**
 * This is the configuration component for custom indicator display.
 * The purpose of this component is to create and manage team's 'IndicatorDisplayConfig' entries.
 *
 * An IndicatorDisplayConfig can be used by the team to change the display of any of its indicators.
 * Its purpose is to visualize the indicator’s chart data differently,
 * by changing the period of a chart’s point, the behaviour of these points
 * and the behaviour of objective points.
 *
 * @vue-prop {Boolean} displayed - If true, show the content
 * @vue-prop {Boolean} advancedMode - Set the default mode to advanced when editing a config
 * @vue-prop {Boolean} detailedIndicatorHint - If true, add a hint on chart period edition to inform the user
 *                                         that the chart range is always 1 year in detailed-indicator
 * @vue-prop {Boolean} showDefaultWheelView - Whether the option to display wheels should be available or not
 *
 * @vue-event {Object} close - Emits the choosen config
 */

import { clone, isEqual, pick } from 'lodash';
import toastr from 'toastr';
import $ from 'jquery';
// State
import { mapState } from 'vuex';
import { FETCH_INDICATOR_DISPLAY_CONFIGS } from '@/store/action-types';
import { GET_INDICATOR_DISPLAY_CONFIGS } from '@/store/mutation-types';
// Components
import CloseButton from '@/components/close-button.vue';
import EditableLabel from '@/components/editable-label.vue';
import EditableLabelRadio from '@/components/editable-label-radio.vue';
import Icon from '@/components/icon.vue';
import InputNumber from '@/components/input-number.vue';
import IntCheckbox from '@/components/int-checkbox.vue';
import RadioGroup from '@/components/radio-group.vue';
import Switcher from '@/components/switcher.vue';
// Mixins
import mixinNeoAssistant from '@/mixins/neo-assistant.vue';
// Utils
import IAHelpStep from '@/utils/classes/IAHelpStep';
import { PERIOD_UNITS_DATA } from '@/utils/main';
import { deleteMedium, editMedium } from '@/assets/icons';


export default {

  name: 'IndicatorDisplayConfig',

  components: {
    CloseButton,
    EditableLabel,
    EditableLabelRadio,
    Icon,
    InputNumber,
    IntCheckbox,
    RadioGroup,
    Switcher,
  },

  mixins: [mixinNeoAssistant],

  props: {
    advancedMode: Boolean,
    detailedIndicatorHint: Boolean,
    displayed: Boolean,
    showDefaultWheelView: Boolean,
  },

  data() {
    return {
      deleteMedium,
      editMedium,
      editingConfig: null,
      configAtLastUpdate: {},
      saving: false,
      emitConfigOnLoad: 0,
      // Config edition mode. 0: simplified => choose from presets ; 1: advanced => all options available
      idcMode: this.advancedMode ? 1 : 0,
      idcModes: [
        this.$t('message.SIMPLIFIED_MODE'),
        this.$t('message.ADVANCED_MODE'),
      ],
      // Dates options
      endDateOptions: [
        { id: 0, label: this.$t('message.IDC_CURRENT_DATE') },
        { id: 1, label: this.$t('message.IDC_CURRENT_DATE_GAP') },
        { id: 2, label: this.$t('message.IDC_FIXED_END_DATE') },
      ],
      bufferEndDateTimeout: null,
    };
  },

  computed: {
    /** Real value of the point unit is a moment-based unit */
    pointUnitId: {
      get() {
        return this.unitValue('pointUnit');
      },
      set(val) {
        this.unitEdit(val, 'pointUnit');
      },
    },
    chartUnitId: {
      get() {
        return this.unitValue('chartUnit');
      },
      set(val) {
        this.unitEdit(val, 'chartUnit');
      },
    },
    increaseUnitId: {
      get() {
        return this.unitValue('increasePeriodUnit');
      },
      set(val) {
        this.unitEdit(val, 'increasePeriodUnit');
      },
    },
    dateGapUnitId: {
      get() {
        return this.unitValue('dateGapUnit');
      },
      set(val) {
        this.unitEdit(val, 'dateGapUnit');
      },
    },
    /** Array: Selectable but non-editable default configs */
    availableDefaultConfigs() {
      return this.showDefaultWheelView
        ? [...this.$store.getters.formIndicDisplayConfigs, this.$store.getters.formWheelDisplayConfig]
        : this.$store.getters.formIndicDisplayConfigs;
    },
    /** Help to better understand the point period fields */
    pointPeriodHint() {
      const period = ['message.N_DAYS', 'message.N_WEEKS', 'message.N_MONTHS'][this.pointUnitId];
      return this.$t('message.IDC_POINT_PERIOD_HINT', {
        period: this.$tc(period, this.editingConfig.pointPeriod, { nb: this.editingConfig.pointPeriod }),
      });
    },
    chartPeriodHint() {
      const period = ['message.N_DAYS', 'message.N_WEEKS', 'message.N_MONTHS'][this.chartUnitId];
      const periodHint = this.$t('message.IDC_CHART_PERIOD_HINT', {
        period: this.$tc(period, this.editingConfig.chartPeriod, { nb: this.editingConfig.chartPeriod }),
      });
      if (!this.detailedIndicatorHint) {
        return periodHint;
      }
      return `${periodHint}<br>${this.$t('message.IDC_DETAILED_INDIC_HINT')}`;
    },
    increasePeriodHint() {
      const period = ['message.N_DAYS', 'message.N_WEEKS', 'message.N_MONTHS'][this.increaseUnitId];
      return this.$t('message.IDC_INCREASE_PERIOD_HINT', {
        period: this.$tc(period, this.editingConfig.increasePeriod, { nb: this.editingConfig.increasePeriod }),
      });
    },
    /** Whether the 'increasingValues' param is true or not */
    valuesIncreasing() {
      return this.editingConfig.increasingValues === 1;
    },
    /** Relevant columns to check equality betweenn configs */
    presetEqualityColumns() {
      return [
        'pointPeriod',
        'pointUnit',
        'chartPeriod',
        'chartUnit',
        'increasingValues',
        'increasingObjectives',
        'increasePeriod',
        'increasePeriodUnit',
        'dateGapFromCurrent',
        'dateGapUnit',
      ];
    },
    allEqualityColumns() {
      return [...this.presetEqualityColumns, 'name', 'chartEndDate'];
    },
    /** String: Readable chartEndDate date */
    chartEndDateFormatted() {
      if (this.editingConfig.chartEndDate === null) {
        return this.$moment().format(this.$t('dateFormat'));
      }
      return this.$moment(this.editingConfig.chartEndDate).format(this.$t('dateFormat'));
    },
    ...mapState({
      configs: state => state[GET_INDICATOR_DISPLAY_CONFIGS.stateKey] || [],
      configsLoading: state => state[GET_INDICATOR_DISPLAY_CONFIGS.loadingKey],
    }),
    /** Predefined config for the demo */
    compConfigs() {
      const demoCfg = Object.assign({}, this.presets[0]);
      demoCfg.name = this.$t('help.IDC_NEW_CFG_NAME');
      return this.isOnDemoMode ? [demoCfg] : this.configs;
    },
    /** Array: Default prsets to select */
    presets() {
      const defaultParams = {
        pointPeriod: 1,
        pointUnit: 'd',
        chartPeriod: 1,
        chartUnit: 'w',
        increasingValues: 0,
        increasingObjectives: 0,
        increasePeriod: 1,
        increasePeriodUnit: 'M',
        chartEndDate: null,
        dateGapFromCurrent: null,
        dateGapUnit: 'd',
      };
      const presets = [
        {
          presetId: 0,
          name: this.$t('message.IDC_PRESET_1'),
          chartPeriod: 12,
          chartUnit: 'M',
          increasingValues: 1,
          increasePeriod: 1,
          increasePeriodUnit: 'M',
        },
        {
          presetId: 1,
          name: this.$t('message.IDC_PRESET_2'),
          chartPeriod: 12,
          chartUnit: 'M',
          increasingValues: 1,
          increasingObjectives: 1,
          increasePeriod: 1,
          increasePeriodUnit: 'M',
        },
        {
          presetId: 2,
          name: this.$t('message.IDC_PRESET_3'),
          pointPeriod: 1,
          pointUnit: 'w',
          chartPeriod: 12,
          chartUnit: 'M',
        },
        {
          presetId: 3,
          name: this.$t('message.IDC_PRESET_4'),
          chartPeriod: 1,
          chartUnit: 'w',
          dateGapFromCurrent: 1,
          dateGapUnit: 'w',
        },
        {
          presetId: 4,
          name: this.$t('message.IDC_PRESET_5'),
          chartPeriod: 1,
          chartUnit: 'w',
          increasingValues: 1,
          increasingObjectives: 1,
          increasePeriod: 1,
          increasePeriodUnit: 'w',
          dateGapFromCurrent: 1,
          dateGapUnit: 'w',
        },
      ];
      return presets.map(preset => ({ ...defaultParams, ...preset }));
    },
    /** Number: Chart date selected option */
    endDateSelectedOption: {
      get() {
        if (this.editingConfig.chartEndDate !== null) {
          return 2;
        }
        if (this.editingConfig.dateGapFromCurrent !== null) {
          return 1;
        }
        return 0;
      },
      set(option) {
        if (option === 0) {
          this.editingConfig.chartEndDate = null;
          this.editingConfig.dateGapFromCurrent = null;
        }
        if (option === 1 && this.editingConfig.dateGapFromCurrent === null) {
          this.editingConfig.chartEndDate = null;
          this.editingConfig.dateGapFromCurrent = 0;
        }
        if (option === 2 && this.editingConfig.chartEndDate === null) {
          this.editingConfig.chartEndDate = this.$moment().format('YYYY-MM-DD');
          this.editingConfig.dateGapFromCurrent = null;
        }
        this.configUpdated();
      },
    },
    /** Number: Value of editingConfig.dateGapFromCurrent */
    dateGapFromCurrent: {
      get() {
        return this.editingConfig.dateGapFromCurrent || 0;
      },
      set(newVal) {
        if (newVal > -1 && this.endDateSelectedOption === 1) {
          this.editingConfig.dateGapFromCurrent = Number(newVal);
          clearTimeout(this.bufferEndDateTimeout);
          this.bufferEndDateTimeout = setTimeout(this.configUpdated, 800);
        }
      },
    },
  },

  watch: {
    configsLoading(loading) {
      if (!loading && this.emitConfigOnLoad) {
        this.emitLoadedConfig(this.emitConfigOnLoad);
        this.emitConfigOnLoad = 0;
      }
    },
    displayed(newVal, oldVal) {
      if (newVal && !oldVal) {
        this.show();
      }
    },
  },

  created() {
    this.refresh();
  },

  methods: {
    /** Fetch available configs */
    refresh() {
      if (!this.configsLoading && this.currentTeam) {
        this.$store.dispatch(FETCH_INDICATOR_DISPLAY_CONFIGS);
      }
    },
    /** Display the content of the component and refresh */
    show() {
      this.editingConfig = null;
      this.refresh();
    },
    /** Creates a new entry and trigger edition */
    newConfig() {
      this.App.post('/server/indicators/displayConfigs/create.php', {}, (data) => {
        if (data && data.id) {
          this.editConfig(data);
        }
      });
    },
    editConfig(config) {
      if (config.disabled) {
        return;
      }
      // Prevent Vuex mutation warning
      this.editingConfig = Object.assign({}, config);
      this.setupDatePicker();
    },
    /** Request an event $emit (when the configs are loaded) with the config */
    requestLoadedConfig(configId) {
      if (this.configsLoading) {
        this.emitConfigOnLoad = configId;
      } else {
        this.emitLoadedConfig(configId);
      }
    },
    /** Emit the config with id = configId */
    emitLoadedConfig(configId) {
      const config = this.configs.find(item => item.id === configId);
      this.$emit('select', config);
    },
    /** Switch between advanced and simplified */
    switchMode(mode) {
      if (mode === 1) {
        this.setupDatePicker();
      }
    },
    setupDatePicker() {
      this.$nextTick(() => {
        $(this.$refs.chartEndDate).datepicker({
          dateFormat: this.$t('datePickerFormat'),
          onSelect: (text, d) => {
            this.editingConfig.chartEndDate = this.$moment({
              year: d.currentYear,
              month: d.currentMonth,
              date: d.currentDay,
            })
              .format('YYYY-MM-DD');
            this.configUpdated();
          },
        });
      });
    },
    /** User clicked a preset => set values */
    presetPicked(preset) {
      Object.assign(this.editingConfig, pick(preset, this.presetEqualityColumns));
      this.configUpdated();
    },
    /** Save the current 'editingConfig' */
    configUpdated() {
      // Minimize server interactions for slow networks (still keeping all the data up to date)
      if (this.saving || this.idcAreEqual(this.editingConfig, this.configAtLastUpdate, true)) {
        return;
      }
      this.saving = true;
      this.App.post('/server/indicators/displayConfigs/set.php', this.editingConfig, (data) => {
        toastr.remove();
        if (data instanceof Object && data.id) {
          this.saving = false;
          if (this.idcAreEqual(this.editingConfig, data, true)) {
            toastr.success(this.$t('message.SAVED'));
            this.configAtLastUpdate = data;
          } else {
            this.configUpdated();
          }
        } else {
          toastr.error(this.$t('message.CONNECTION_ERROR'));
        }
      });
    },
    /** Check equality between two objects using relevant columns */
    idcAreEqual(config, other, all) {
      const columns = all ? this.allEqualityColumns : this.presetEqualityColumns;
      return isEqual(pick(config, columns), pick(other, columns));
    },
    /** User is done editing the config */
    editionDone() {
      this.editingConfig = null;
      this.refresh();
    },
    /** Select a config from which to build the chart */
    selectConfig(config) {
      this.$emit('select', config);
      this.onClose();
    },
    /** Removes a config permanently */
    deleteConfig(config) {
      if (config.disabled) {
        return;
      }
      this.App.post('/server/indicators/displayConfigs/delete.php', { id: config.id }, (data) => {
        toastr.remove();
        if (data instanceof Array) {
          this.refresh();
        } else {
          toastr.error(this.$t('message.CONNECTION_ERROR'));
        }
      });
    },
    /** Available periods of time for one point */
    periodItems(key) {
      const val = (key && this.editingConfig[key]) || 0;
      return PERIOD_UNITS_DATA.map(unit => ({ ...unit, unit: this.$tc(unit.unitTr, val) }));
    },
    /** Real value of the point unit is a moment-based unit */
    unitValue(unitKey) {
      return PERIOD_UNITS_DATA.findIndex(item => item.letter === this.editingConfig[unitKey]);
    },
    unitEdit(val, unitKey) {
      this.editingConfig[unitKey] = PERIOD_UNITS_DATA[val].letter;
    },
    onClose() {
      this.$emit('close');
    },
    clone,
    isEqual,
    pick,
    createHelpSteps() {
      return [
        new IAHelpStep(null, this.$t('help.IDC_INTRO')),
        new IAHelpStep('.idc-config-new', this.$t('help.IDC_NEW'), () => {
          this.editConfig(this.presets[0]);
        }),
        new IAHelpStep('.idc-edition .editable-label', this.$t('help.IDC_NAME'), () => {
          this.editingConfig.name = this.$t('help.IDC_NEW_CFG_NAME');
        }),
        new IAHelpStep('.idc-edition .switcher', this.$t('help.IDC_MODES'), () => {
          this.idcMode = 1;
        }),
        new IAHelpStep('.idc-edition .advanced-form', this.$t('help.IDC_ADV')),
        new IAHelpStep('.idc-edition .checkbox', this.$t('help.IDC_ADV_INCR'), () => {
          this.editingConfig.increasingValues = 1;
        }),
        new IAHelpStep('.idc-edition .increase-interval .point-period', this.$t('help.IDC_ADV_INCR_PERIOD')),
        new IAHelpStep('.idc-edition .increase-interval .checkbox', this.$t('help.IDC_ADV_OBJ_INCR')),
        new IAHelpStep('.idc-edition .config-start', this.$t('help.IDC_START'), () => {
          this.editionDone();
        }),
        new IAHelpStep('.idc-config', this.$t('help.IDC_CLICK')),
      ];
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.indicator-display-config.dark-overlay {
  text-align: left;

  .dark-overlay-content {
    background: #f4f4f4;
    padding: 10px 0px;

    .indicator-display-config__content {
      padding-bottom: 1.5em;

      &__wrapper {
        padding: 0 $page-content-padding;
        box-sizing: border-box;
      }
    }

    h2 {
      text-align: center;
      padding: 0;
      margin: 1rem 0 2rem 0;

      &:after {
        margin: 0;
      }
    }

    .idc-config, .idc-preset {
      padding: 5px 10px;
      margin-top: 5px;
      text-align: left;
      cursor: pointer;

      .name {
        min-width: 0px;
        flex: 1;
      }

      &__delete {
        margin: 0 0 0 1rem;
      }
    }

    .idc-config:last-child {
      display: inline-flex;
      margin-bottom: 20px;
    }

    .idc-preset.selected {
      border: solid 1px $color-shizen-blue-border-hover;
    }

    .idc-edition {

      &__wrapper {
        background: #fff;
        margin: $page-content-padding;
        border-radius: $high-border-radius;
        padding: .8rem .7rem;
      }

      > .editable-label textarea {
        white-space: nowrap;
        max-height: 2em;
        resize: none;
      }
      .advanced-form {
        margin-top: .5rem;

        input, .editable-label {
          border: 1px gray dashed;
          padding: 0px 5px;
          line-height: 1.5em;
          background: #fff;
        }
      }

      .switcher {
        margin: 1rem 0;
      }

      .point-period {
        .input-number {
          width: 2em;
          font-size: 1em;
          color: inherit;
        }

        .editable-label {
          display: inline-block;
        }
      }
    }
  }

  .indicator-display-config__done {
    margin: 1em 0;
  }

  .idc__end-date {
    .input-number {
      width: 2em;
    }
    input {
      color: $color-main-text;
    }
  }

  .idc__footer {
    position: relative;

    .saved-indicator {
      position: absolute;
      bottom: 5px;
      right: 10px;

      img {
        width: 20px;
        height: 20px;
      }
    }
  }
}

@media (max-width: $smartphone-landscape) {
  .indicator-display-config.dark-overlay {
    .dark-overlay-content {
      .idc-config, .idc-preset {
        > img {
          padding: 5px;
          width: 17px;
        }
      }
    }
  }
}
</style>
